import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@clerk/react';
import { StatusToast, StatusToastVariant } from '@this+that/ui';
import log from 'loglevel';

import { useCustomToast } from './useCustomToast';
import type { ComposeSnapshot } from '../models/composeSnapshot';
import { useMessageDialogStore } from '../store/MessageDialog/useMessageDialogStore';
import { listDirtyComposeSnapshots } from '../store/utils/composeSnapshotCache';
import {
  COMPOSE_RECOVERY_TOAST_DURATION_MS,
  COMPOSE_SNAPSHOT_ORPHAN_GRACE_MS,
} from '../utils/constants/drafts';

const LOG_MESSAGES = {
  LISTING_FAILED: 'Unsent compose recovery listing failed',
} as const;

const findOrphans = async (
  userId: string,
  excludedSessionIds: ReadonlySet<string>,
): Promise<ComposeSnapshot[]> => {
  try {
    const snapshots = await listDirtyComposeSnapshots(userId);
    const now = Date.now();
    return snapshots.filter(
      ({ sessionId, updatedAt }) =>
        !excludedSessionIds.has(sessionId) &&
        now - updatedAt >= COMPOSE_SNAPSHOT_ORPHAN_GRACE_MS,
    );
  } catch (error) {
    log.warn(LOG_MESSAGES.LISTING_FAILED, error);
    return [];
  }
};

const openSnapshot = (snapshot: ComposeSnapshot): void => {
  void useMessageDialogStore.getState().open({
    draftId: snapshot.draftId,
    messageId: snapshot.sourceMessageId,
    selectedHandlerButton: snapshot.handler,
    inboxEntryId: snapshot.inboxEntryId,
    restoreSnapshotSessionId: snapshot.sessionId,
  });
};

export const useUnsentComposeRecovery = (): void => {
  const { user } = useUser();
  const userId = user?.id;
  const { t } = useTranslation();
  const { customToaster } = useCustomToast();
  const isDialogOpen = useMessageDialogStore((state) => state.isOpen);

  const showToastRef = useRef(customToaster);
  showToastRef.current = customToaster;
  const translateRef = useRef(t);
  translateRef.current = t;

  const hasEvaluatedRef = useRef(false);
  const shouldReevaluateRef = useRef(false);
  const recoveredSessionIdsRef = useRef(new Set<string>());

  const recover = useCallback((snapshot: ComposeSnapshot) => {
    recoveredSessionIdsRef.current.add(snapshot.sessionId);
    shouldReevaluateRef.current = true;
    openSnapshot(snapshot);
  }, []);

  const offerRecovery = useCallback(
    async (currentUserId: string) => {
      const orphans = await findOrphans(
        currentUserId,
        recoveredSessionIdsRef.current,
      );
      if (orphans.length === 0) {
        return;
      }
      const translate = translateRef.current;
      showToastRef.current(
        <StatusToast
          status={StatusToastVariant.Warning}
          message={translate('inbox.messageDialog.unsentRecovery.message', {
            count: orphans.length,
          })}
          action={{
            label: translate('inbox.messageDialog.unsentRecovery.recover'),
            onClick: () => recover(orphans[0]),
          }}
          durationMs={COMPOSE_RECOVERY_TOAST_DURATION_MS}
          dataTestId="unsent-compose-recovery-toast"
        />,
        undefined,
        COMPOSE_RECOVERY_TOAST_DURATION_MS,
      );
    },
    [recover],
  );

  useEffect(() => {
    if (!userId || isDialogOpen) {
      return;
    }
    if (hasEvaluatedRef.current && !shouldReevaluateRef.current) {
      return;
    }
    hasEvaluatedRef.current = true;
    shouldReevaluateRef.current = false;
    void offerRecovery(userId);
  }, [userId, isDialogOpen, offerRecovery]);
};
